/**
 * QR Login Page
 *
 * Auto-logs in when opened with ?code=XXXXXX (from printed QR labels).
 * Falls back to manual six-digit code entry if the code is missing or invalid.
 */

import React, { useContext, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { navigate } from '../utils/navigate'
import API from '../utils/api'
import { getUserID, setUser, isLoggedIn } from '../utils/auth'
import AppContext from '../utils/context'
import { Fade } from '../components/reveal'
import { Rotate } from '../components/reveal'
import ImageWatchHand from '../components/images/watch-hand'
import WatsonLogoSVG from '../components/svg/logo'
import Background from '../components/background'
import { setTeamData, getCurrentTeamData } from '../utils/team-data'
import { updateHuntData } from '../utils/hunt-data'
import ReactCodeInput from 'react-code-input'

import '../sass/app/_login.scss'

const QRLoginPage = () => {
  const context = useContext(AppContext)
  const [searchParams] = useSearchParams()
  const [access_code, setAccessCode] = useState(``)
  const codeRef = useRef()
  const autoSubmitted = useRef(false)

  const site_data = context.site_data

  const submitCode = code => {
    if (!code) {
      return
    }

    code = code.toLowerCase()
    context.setLoading(true)

    API.get(`session/${code}/`)
      .then(res => {
        const userId = getUserID()
        setTeamData(res.data.team)
        setUser({
          guid: userId,
        })
        updateHuntData(res.data.team.hunt_id, res.data.team.start, res.data.team.direction).then(() => {
          const teamData = getCurrentTeamData()
          if (teamData.start_time) {
            navigate(`/settings/`)
          } else {
            navigate(`/`)
          }
        })
      })
      .catch(error => {
        context.setLoading(false)
        let error_msg = error
        if (error.response !== undefined) {
          error_msg = error.response.status
        }
        if (codeRef.current) {
          codeRef.current.clear()
        }
        context.showAlert(`Incorrect Code!`)
        console.error(error_msg)
      })
  }

  // Auto-submit when opened via QR URL (?code=...)
  useEffect(() => {
    const code = searchParams.get(`code`)
    if (code && !autoSubmitted.current) {
      autoSubmitted.current = true
      submitCode(code)
      return
    }

    if (!code && isLoggedIn()) {
      navigate(`/`)
    }
    // Intentionally run once on mount for the query param.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleUpdate = code => {
    setAccessCode(code)
    if (code.length == 6) {
      submitCode(code)
    }
  }

  const handleSubmit = event => {
    event.preventDefault()
    submitCode(access_code)
  }

  return (
    <Fade>
      <div className={`login-page`} style={{ minHeight: `100vh`, overflow: `hidden`, maxWidth: `100%` }}>
        <Background alt="Watson Login Screen" filename="login.jpg" />

        <div
          style={{
            margin: `auto`,
            position: `relative`,
            zIndex: `10`,
          }}>
          <div>
            {site_data.greeting_text && (
              <>
                <Fade top big delay={1000}>
                  <div
                    className={`py-4 px-4 mb-5`}
                    style={{
                      backgroundColor: `#F5F2ED`,
                      boxShadow: `2px 2px 5px rgba(189, 186, 186, 0.5)`,
                    }}>
                    <div
                      style={{
                        maxWidth: `400px`,
                        margin: `auto`,
                      }}>
                      <WatsonLogoSVG />
                    </div>
                  </div>
                </Fade>
                <div
                  style={{
                    maxWidth: `400px`,
                    margin: `auto`,
                  }}>
                  <Fade left big delay={1000}>
                    <h1 className={`text-center text-uppercase text-white h1 title mb-3 px-3`}>
                      Ask your Team Captain for the six-digit code
                    </h1>
                  </Fade>
                  <Fade right big delay={1500}>
                    <h6
                      className={`h6 lead text-center text-white mb-5 px-4`}
                      style={{
                        marginTop: `-10px`,
                        lineHeight: `1.4`,
                        fontStyle: `italic`,
                      }}>
                      It’s below the QR code.{` `}
                    </h6>
                  </Fade>
                </div>
              </>
            )}

            <Fade bottom delay={2000}>
              <form className={`access-form-group text-center`} method="post" onSubmit={handleSubmit}>
                <div className="form-group text-center py-3 d-inline-block px-3">
                  <ReactCodeInput
                    ref={codeRef}
                    type="tel"
                    pattern="[0-9]*"
                    fields={6}
                    onChange={handleUpdate}
                    label={`Enter the 6 digit code`}
                    novalidate
                  />
                </div>

                <button type="submit" className="sr-only">
                  Submit
                </button>
              </form>
              <div className="text-center text-white">
                <small className="copyright" dangerouslySetInnerHTML={{ __html: site_data.copyright_text }} />
              </div>
            </Fade>
          </div>
        </div>

        <Rotate
          top
          right
          delay={2500}
          style={{
            position: `absolute`,
            right: `0px`,
            bottom: `60px`,
          }}>
          <ImageWatchHand />
        </Rotate>
      </div>
    </Fade>
  )
}

export default QRLoginPage
